import {
    BackofficeCustomerParams,
    Customer
} from "../../../Models/API/BackofficeModel"
import { apiEndpoints } from "../../../Routes/api"
import { safePromise } from "../../../Helpers/Functions/promise"
import { apiFetch } from "../../Client/NetworkActions"
import { MainThunkDispatch, State } from "../../../Reducers"
import { BackofficeType } from "../../Types"
import { failMessage, successMessage } from "../../Client/MessagesActions"
import { AppInstanceListParams } from "../../../Models/API/AppInstanceModel"
import { hasMore } from "../../../Models/Client/RemoteData"

export const getCustomerList =
    (params: BackofficeCustomerParams, page = 0) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getCustomers({
            ...params,
            size: 20,
            page
        })

        const { data, error } = await safePromise(
            dispatch(
                apiFetch<{ customers: Customer[]; total: number }>(
                    endpoint.url,
                    endpoint
                )
            )
        )

        if (error !== undefined || data === undefined) {
            return
        }

        // currently cognito does not provide total users and also does not provide current page number,
        // it provides a paginationToken to query next page (currently not in use)
        dispatch({
            type: BackofficeType.setCustomers,
            meta: {
                total: data.total,
                page,
                params
            },
            payload: data.customers
        })
    }
export const loadMoreCustomerList =
    () => async (dispatch: MainThunkDispatch, getState: () => State) => {
        const customers = getState().api.backoffice.customers

        if (
            customers !== undefined &&
            customers.list !== undefined &&
            hasMore(customers)
        ) {
            await dispatch(
                getCustomerList(
                    customers.list.params,
                    customers.list.pages.length
                )
            )
        }
    }
export const getCustomer =
    (username: string) => async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getCustomer({
            username
        })

        const { data, error } = await safePromise(
            dispatch(apiFetch<Customer>(`GET_CUSTOMER_${username}`, endpoint))
        )

        if (error !== undefined || data === undefined) {
            return
        }

        dispatch({
            type: BackofficeType.setCustomer,
            meta: {
                username
            },
            payload: data
        })
    }
export const extendCustomerTrial =
    (username: string, trialDays?: number, convertToFullUser?: boolean) =>
    async (dispatch: MainThunkDispatch) => {
        const endpoint = apiEndpoints.getCustomer({
            username
        })

        if (trialDays === undefined && convertToFullUser === undefined) {
            dispatch(failMessage("Either extend trial or convert to full user"))
            return
        }
        const { data, error } = await safePromise(
            dispatch(
                apiFetch(
                    `EXTEND_CUSTOMER_TRIAL_${username}`,
                    endpoint,
                    {
                        method: "PATCH",
                        body: JSON.stringify({
                            trialDays,
                            convertToFullUser
                        })
                    },
                    undefined,
                    true
                )
            )
        )

        if (error !== undefined) {
            dispatch(
                failMessage(
                    trialDays !== undefined
                        ? "Failed to extend trial"
                        : "Failed to convert to full user",
                    error
                )
            )
            throw error
        }
        if (data === undefined) {
            return
        }

        dispatch(
            successMessage(
                trialDays !== undefined
                    ? "Successfully extended trial"
                    : "Successfully converted to full user"
            )
        )
    }

export const getCustomerAppList =
    (
        customer: Customer,
        params: AppInstanceListParams,
        page: number = 0,
        pageSize: number = 50
    ) =>
    async (dispatch: MainThunkDispatch) => {
        const allParams = {
            customerEmail: customer.email,
            ...params,
            page,
            pageSize
        }
        const endpoint = apiEndpoints.getCustomerAppList(allParams)

        const { data, error } = await safePromise(
            dispatch(apiFetch(endpoint.url, endpoint))
        )

        if (error !== undefined || data === undefined || !data.apps) {
            return
        }

        dispatch({
            type: BackofficeType.setCustomerApps,
            meta: {
                username: customer.username,
                total: data.total,
                page,
                params
            },
            payload: data.apps
        })
    }

export const loadMoreCustomerAppList =
    (customer: Customer) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const data = getState().api.backoffice.customerApps[customer.username]

        if (data?.list !== undefined && hasMore(data)) {
            await dispatch(
                getCustomerAppList(
                    customer,
                    data.list.params,
                    data.list.pages.length
                )
            )
        }
    }
